import React, { useState, useEffect, useContext } from 'react'
import ApiService from '../../services/ApiService'
import close from '../../assets/close-icon.svg'
import plus from '../../assets/productions/plus.svg'
import ModalContext from '../../contexts/ModalContext'
import { contactInputs, deliveryAddressInputs, sectionsInModal } from './utils'
import DisponibilityComponent from './DisponibilityComponent'
import SelectFunctionsComponent from './SelectFunctionComponent'
import SelectTypeComponent from './SelectTypeComponent'
import InsertCEPComponent from './InsertCEPComponent'
import { FeedbackContext } from '../../contexts/FeedbackContext'
import ModalTest from '../BaseModal'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import ConfirmationModal from '../ModalConfirmation'
import { Plus, Save } from 'lucide-react'
import { OptionsContainer } from '../selectSearch/selectOptions/style'
import { getSearchParamsForLocation } from 'react-router-dom/dist/dom'
import UserSelect from '../../pages/ClientPanel/Equipment/Select/UserSelect'
import {
  Container, Header, CloseDiv, SwitchDiv, InputLabel, SwitchBox,
  SwitchInput, SwitchSlider, FormDataDiv, SectionDiv, InputDiv, InputData,
  LineContainer, ContactsContainer, EmptyContactsMessage, ContactCard,
  ContactHeader, ContactName, ContactInfo, ContactField, ContactLabel, LinkedUsersTagsContainer,
  ContactValue, BottomDiv, ButtonContainer, ButtonDiv, LinkedUserTag, SectionHeaderRow,
  SectionTitle, CompactUserSelectWrap
} from './style'
import { ContactInterface, CompanyInterface, DeliveryAddressInterface, functionsType, LinkedUserInterface, typesInterface } from './types'

const emptyDeliveryAddressForm = (): DeliveryAddressInterface => ({
  cep: '',
  state: { id: 0, name: '' },
  city: { id: 0, name: '' },
  neighborhood: { id: 0, name: '' },
  street: '',
  number: '',
  complement: '',
})

const hasValidDeliveryAddress = (addr: DeliveryAddressInterface) => {
  return !!(
    addr.cep?.trim() ||
    addr.state?.name?.trim() ||
    addr.city?.name?.trim() ||
    addr.neighborhood?.name?.trim() ||
    addr.street?.trim() ||
    addr.number?.trim() ||
    addr.complement?.trim()
  )
}

const formatDeliveryAddressSummary = (addr: DeliveryAddressInterface) => {
  const parts: string[] = []
  if (addr.street) parts.push(addr.street)
  if (addr.number) parts.push(`nº ${addr.number}`)
  const location = [addr.neighborhood?.name, addr.city?.name, addr.state?.name].filter(Boolean).join(', ')
  if (location) parts.push(location)
  if (addr.cep) parts.push(addr.cep)
  return parts.join(' — ') || 'Endereço sem detalhes'
}

const buildDeliveryAddressesPayload = (
  addresses: DeliveryAddressInterface[],
  formatCep: (value: string) => string,
) =>
  addresses
    .filter(hasValidDeliveryAddress)
    .map((addr) => ({
      ...(addr.id ? { id: addr.id } : {}),
      cep: formatCep(addr.cep.trim()),
      state: addr.state,
      city: addr.city,
      neighborhood: addr.neighborhood,
      street: addr.street.trim(),
      number: addr.number.trim(),
      complement: addr.complement.trim(),
    }))

interface AddCompanyModalProps {
  onSuccess?: (createdCompany: any) => void;
  onCancel?: () => void;
  initialName?: string;
  initialCnpjCpf?: string;
  initialType?: string;
}

const AddCompanyModal: React.FC<AddCompanyModalProps> = ({
  onSuccess,
  onCancel,
  initialName = '',
  initialCnpjCpf = '',
  initialType = '',
}) => {
  const { setModalActive } = useContext(ModalContext)
  const { setFeedback, setNewCompanies } = useContext(FeedbackContext)
  const [newContact, setNewContact] = useState<boolean>(false)
  const [newDeliveryAddress, setNewDeliveryAddress] = useState<boolean>(false)
  const [legalPerson, setLegalPerson] = useState<boolean>(true)
  const [isMain, setIsMain] = useState<boolean>(false)
  const [autoAddress, setAutoAddress] = useState({ state: false, city: false, neighborhood: false, street: false })
  const [autoDeliveryAddress, setAutoDeliveryAddress] = useState({ state: false, city: false, neighborhood: false, street: false })
  const [searchParam] = useSearchParams();
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const legalPersonInputs = ['stateRegistration', 'cnpj', 'companyName']
  const [contactForm, setContactForm] = useState<ContactInterface>({
    name: '',
    email: '',
    phone: '',
    companyId: 0
  })
  const [deliveryAddressForm, setDeliveryAddressForm] = useState<DeliveryAddressInterface>(emptyDeliveryAddressForm())

  const companyId = searchParam.get('id')
  const [showConfirm, setShowConfirm] = useState<boolean>(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [userSelectKey, setUserSelectKey] = useState<number>(0)

  const [formData, setFormData] = useState<CompanyInterface>({
    cnpj: '',
    cpf: '',
    companyName: '',
    stateRegistration: '',
    contribuinteIcms: '',
    contacts: [],
    deliveryAddresses: [],
    linkedUsers: [],
    contactName: '',
    email: '',
    phone: '',
    activedDays: [],
    startTime: '',
    endTime: '',
    functions: [],
    companyTypes: [],
    cep: '',
    state: { id: 0, name: '' },
    city: { id: 0, name: '' },
    neighborhood: { id: 0, name: '' },
    street: '',
    complement: '',
    number: ''
  })

  useEffect(() => {
    if (companyId) return;
    const digits = initialCnpjCpf.replace(/\D/g, '');
    const isCpf = digits.length === 11;
    const isJuridica = digits.length > 0 ? !isCpf : true;

    setLegalPerson(isJuridica);

    let typeId = 1;
    let typeName = 'Cliente';
    if (initialType === 'Fornecedor') {
      typeId = 2;
      typeName = 'Fornecedor';
    } else if (initialType === 'Transportadora') {
      typeId = 3;
      typeName = 'Transportadora';
    }

    setFormData(prev => ({
      ...prev,
      cnpj: isJuridica ? initialCnpjCpf : '',
      cpf: !isJuridica ? initialCnpjCpf : '',
      companyName: isJuridica ? initialName : '',
      contactName: !isJuridica ? initialName : '',
      companyTypes: [{ id: typeId, typeName }],
    }));
  }, [initialName, initialCnpjCpf, initialType, companyId]);

  useEffect(() => {
    const getCompany = async () => {

      console.log('🛰️ [FRONT] getCompany chamado com id:', companyId)
      const response = await ApiService.companies.findById(Number(companyId))

      console.log('🛰️ [FRONT] resposta getCompany:', response?.status, response?.data)

      if (response && response.data) {
        const company = response.data
        const rawCompanyTypes = Array.isArray(company.companyTypes) ? company.companyTypes : []

        const flattenedTypes = rawCompanyTypes.map((ct: any) => {
          const id = ct?.CompanyType?.id ?? ct?.companyType?.id ?? ct?.companyTypeId ?? ct?.id ?? ct
          const typeName = String(ct?.CompanyType?.typeName ?? ct?.companyType?.typeName ?? ct?.typeName ?? '').trim()
          return { id, typeName }
        }).filter((ct: any) => ct.id !== undefined && ct.id !== null)

        const normalizedCompanyTypes = flattenedTypes

        console.log('🛰️ [FRONT] rawCompanyTypes:', rawCompanyTypes)
        console.log('🛰️ [FRONT] flattenedTypes:', flattenedTypes)
        console.log('🛰️ [FRONT] normalizedCompanyTypes:', normalizedCompanyTypes)

        const rawCepValue = String(company.cep ?? company.address?.cep ?? '').replace(/\D/g, '')
        const paddedCep = rawCepValue ? rawCepValue.padStart(8, '0') : ''
        const isMainVal = company.isMain || false
        const isPessoaJuridica = company.legalPerson !== undefined
          ? company.legalPerson
          : (isMainVal || !!company.cnpj || (!!company.companyName && !company.cpf))
        setLegalPerson(isPessoaJuridica)

        const normalizedCompanyForForm = {
          cnpj: company.cnpj || '',
          cpf: company.cpf || '',
          companyName: isPessoaJuridica ? (company.companyName || '') : '',
          stateRegistration: isPessoaJuridica ? (company.stateRegistration || '') : '',
          contribuinteIcms: isPessoaJuridica ? (company.contribuinteIcms || '') : '',
          contacts: company.contacts || [],
          deliveryAddresses: (company.deliveryAddresses || []).map((addr: DeliveryAddressInterface) => ({
            id: addr.id,
            cep: String(addr.cep || '').replace(/\D/g, ''),
            state: { id: addr.state?.id || 0, name: addr.state?.name || '' },
            city: { id: addr.city?.id || 0, name: addr.city?.name || '' },
            neighborhood: { id: addr.neighborhood?.id || 0, name: addr.neighborhood?.name || '' },
            street: addr.street || '',
            complement: addr.complement || '',
            number: addr.number || '',
          })),
          linkedUsers: Array.isArray(company.linkedUsers)
            ? company.linkedUsers
              .filter((user: any) => user?.id)
              .map((user: any) => ({ id: Number(user.id), name: String(user.name || '').trim() }))
              .filter((user: LinkedUserInterface) => user.name.length > 0)
            : [],
          contactName: !isPessoaJuridica ? (company.contactName || company.companyName || '') : (company.contactName || ''),
          email: company.email || '',
          phone: company.phone || '',
          activedDays: company.activedDays || [],
          startTime: company.startTime || '',
          endTime: company.endTime || '',
          functions: company.functions || [],
          companyTypes: normalizedCompanyTypes || [],
          cep: paddedCep,
          state: { id: company.state?.id || 0, name: company.state?.name || '' },
          city: { id: company.city?.id || 0, name: company.city?.name || '' },
          neighborhood: { id: company.neighborhood?.id || 0, name: company.neighborhood?.name || '' },
          street: company.street || '',
          complement: company.complement || '',
          number: company.number || ''
        }

        setFormData(normalizedCompanyForForm)
        setAutoAddress({ state: false, city: false, neighborhood: false, street: false })
        setIsMain(isMainVal)

      }
    }
    if (companyId) {
      getCompany()

    }
  }, [])

  const handleEditForm = (e: any, field: keyof CompanyInterface) => {
    if (field === 'state') {
      setAutoAddress(prev => ({ ...prev, state: false, city: false, neighborhood: false }))
      return setFormData(prev => ({
        ...prev,
        state: { id: 0, name: e.target.value },
        city: { id: 0, name: prev.city.name },
        neighborhood: { id: 0, name: prev.neighborhood.name }
      }))
    }
    if (field === 'city') {
      setAutoAddress(prev => ({ ...prev, city: false, neighborhood: false }))
      return setFormData(prev => ({
        ...prev,
        city: { id: 0, name: e.target.value },
        neighborhood: { id: 0, name: prev.neighborhood.name }
      }))
    }
    if (field === 'neighborhood') {
      setAutoAddress(prev => ({ ...prev, neighborhood: false }))
      return setFormData(prev => ({
        ...prev,
        neighborhood: { id: 0, name: e.target.value }
      }))
    }
    if (field === 'street') {
      setAutoAddress(prev => ({ ...prev, street: false }))
    }
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }))
  }

  const handleCnpjBlur = async () => {
    const digits = (formData.cnpj || '').replace(/\D/g, '')
    if (digits.length !== 14) return

    setFeedback({
      isOpen: true,
      message: 'Consultando dados do CNPJ na Receita Federal...',
      statusOk: true
    })

    try {
      const response = await ApiService.companies.getCnpjDetails(digits)
      if (response && response.data) {
        const details = response.data
        const defaultContribuinte = details.stateRegistration && details.stateRegistration.trim()
          ? 'CONTRIBUINTE'
          : 'NAO_CONTRIBUINTE'

        const neighborhoodStr = typeof details.neighborhood === 'object'
          ? (details.neighborhood?.name || '')
          : (details.neighborhood || '')
        const cityStr = typeof details.city === 'object'
          ? (details.city?.name || '')
          : (details.city || '')
        const stateStr = typeof details.state === 'object'
          ? (details.state?.name || details.state?.sigla || '')
          : (details.state || '')

        setFormData(prev => ({
          ...prev,
          companyName: details.companyName || prev.companyName,
          contactName: details.contactName || prev.contactName,
          email: details.email || prev.email,
          phone: details.phone || prev.phone,
          stateRegistration: details.stateRegistration || prev.stateRegistration,
          contribuinteIcms: defaultContribuinte,
          cep: details.cep ? String(details.cep).replace(/\D/g, '') : prev.cep,
          street: details.street || prev.street,
          number: details.number || prev.number,
          complement: details.complement || prev.complement,
          state: stateStr ? { id: prev.state?.id || 0, name: stateStr } : prev.state,
          city: cityStr ? { id: prev.city?.id || 0, name: cityStr } : prev.city,
          neighborhood: neighborhoodStr ? { id: prev.neighborhood?.id || 0, name: neighborhoodStr } : prev.neighborhood,
        }))

        setAutoAddress({ state: false, city: false, neighborhood: false, street: false })

        const labelEstab = details.tipoEstabelecimento ? ` (${details.tipoEstabelecimento})` : ''

        setFeedback({
          isOpen: true,
          message: `Dados do CNPJ preenchidos com sucesso!${labelEstab}`,
          statusOk: true
        })
      }
    } catch (error: any) {
      console.error('🔎 [FRONT] erro ao consultar CNPJ', error)
      const errorMsg = error?.response?.data?.message || 'Não foi possível validar o CNPJ com a Receita Federal.'
      setFeedback({
        isOpen: true,
        message: errorMsg,
        statusOk: false
      })
    }
  }

  const handleViewValue = (field: keyof CompanyInterface) => {
    if (field === 'state' || field === 'city' || field === 'neighborhood')
      return formData[field].name

    return formData[field]
  }

  const maskCNPJ = (value: any) => {
    return value
      .replace(/\D/g, '')
      .replace(/^(\d{2})(\d)/, '$1.$2')
      .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d)/, '.$1/$2')
      .replace(/(\d{4})(\d)/, '$1-$2')
      .slice(0, 18)
  }

  const maskCPF = (value: any) => {
    return value
      .replace(/\D/g, '')
      .replace(/^(\d{3})(\d)/, '$1.$2')
      .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d)/, '.$1-$2')
      .slice(0, 14)
  }

  const maskPhone = (value: any) => {
    return value
      .replace(/\D/g, '')
      .replace(/^(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{4,5})(\d{4})$/, '$1-$2')
      .slice(0, 15)
  }

  const formatCep = (value: string) => {
    const raw = (value || '').replace(/\D/g, '')
    const digits = raw ? raw.padStart(8, '0').slice(0, 8) : ''
    return digits.replace(/^(\d{5})(\d)/, '$1-$2')
  }

  const handleNewContact = () => {
    setNewContact(true)
  }

  const handleAddContact = () => {
    console.log('🛰️ [FRONT] handleAddContact', contactForm)
    setFormData(prev => ({
      ...prev,
      contacts: [
        ...prev.contacts,
        {
          name: contactForm.name,
          email: contactForm.email,
          phone: contactForm.phone,
          companyId: companyId ? parseInt(companyId) : 0
        }
      ]
    }))
    setContactForm(
      {
        name: '',
        email: '',
        phone: '',
        companyId: 0
      }
    )
    setNewContact(false)
  }

  const handleEditContat = (field: string, value: string) => {
    setContactForm((prevData) => ({
      ...prevData,
      [field]: value
    }))
  }

  const handleRemoveContact = (index: number) => {
    setFormData(prev => ({
      ...prev,
      contacts: prev.contacts.filter((_, i) => i !== index)
    }))
  }

  const handleNewDeliveryAddress = () => {
    setNewDeliveryAddress(true)
    setDeliveryAddressForm(emptyDeliveryAddressForm())
    setAutoDeliveryAddress({ state: false, city: false, neighborhood: false, street: false })
  }

  const handleEditDeliveryAddressField = (field: keyof DeliveryAddressInterface, value: string) => {
    if (field === 'state') {
      setAutoDeliveryAddress(prev => ({ ...prev, state: false, city: false, neighborhood: false }))
      setDeliveryAddressForm(prev => ({
        ...prev,
        state: { id: 0, name: value },
        city: { id: 0, name: prev.city.name },
        neighborhood: { id: 0, name: prev.neighborhood.name },
      }))
      return
    }
    if (field === 'city') {
      setAutoDeliveryAddress(prev => ({ ...prev, city: false, neighborhood: false }))
      setDeliveryAddressForm(prev => ({
        ...prev,
        city: { id: 0, name: value },
        neighborhood: { id: 0, name: prev.neighborhood.name },
      }))
      return
    }
    if (field === 'neighborhood') {
      setAutoDeliveryAddress(prev => ({ ...prev, neighborhood: false }))
      setDeliveryAddressForm(prev => ({
        ...prev,
        neighborhood: { id: 0, name: value },
      }))
      return
    }
    if (field === 'street') {
      setAutoDeliveryAddress(prev => ({ ...prev, street: false }))
    }
    setDeliveryAddressForm(prev => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleAddDeliveryAddress = () => {
    if (!hasValidDeliveryAddress(deliveryAddressForm)) {
      setFeedback({
        isOpen: true,
        message: 'Preencha ao menos um campo do endereço de entrega.',
        statusOk: false,
      })
      return
    }

    setFormData(prev => ({
      ...prev,
      deliveryAddresses: [...prev.deliveryAddresses, { ...deliveryAddressForm }],
    }))
    setDeliveryAddressForm(emptyDeliveryAddressForm())
    setAutoDeliveryAddress({ state: false, city: false, neighborhood: false, street: false })
    setNewDeliveryAddress(false)
  }

  const handleRemoveDeliveryAddress = (index: number) => {
    setFormData(prev => ({
      ...prev,
      deliveryAddresses: prev.deliveryAddresses.filter((_, i) => i !== index),
    }))
  }

  const handleAddLinkedUser = (user: any | null) => {
    if (!user?.id || !user?.name) return

    setFormData(prev => {
      const alreadyLinked = prev.linkedUsers.some(linkedUser => linkedUser.id === user.id)
      if (alreadyLinked) return prev

      return {
        ...prev,
        linkedUsers: [...prev.linkedUsers, { id: Number(user.id), name: String(user.name).trim() }]
      }
    })

    setUserSelectKey(prev => prev + 1)
  }

  const handleRemoveLinkedUser = (id: number) => {
    setFormData(prev => ({
      ...prev,
      linkedUsers: prev.linkedUsers.filter(user => user.id !== id)
    }))
  }

  const handleDeleteConfirm = async () => {
    if (!companyId) return
    try {
      await ApiService.companies.delete(Number(companyId))
      setShowConfirm(false)
      setModalActive(false)
      setNewCompanies?.(prev => prev + 1)
      queryClient.invalidateQueries({ queryKey: ['companies'] })
      setFeedback({ isOpen: true, message: 'Empresa excluída com sucesso!', statusOk: true })
      navigate('..')
    } catch (error: any) {
      console.error('Erro ao excluir empresa:', error)
      const msg = error?.response?.data?.message || error?.response?.data?.error || 'Não foi possível excluir a empresa. Ela pode possuir relações com outros registros.'
      setShowConfirm(false)
      setFeedback({ isOpen: true, message: msg, statusOk: false })
    }
  }

  const handleEdit = () => {
    if (legalPerson) {
      if (formData.cnpj.trim() && !formData.companyName.trim()) {
        setFeedback({
          isOpen: true,
          message: 'Quando CNPJ é preenchido, a razão social é obrigatória.',
          statusOk: false
        })
        return
      }

      if (!formData.cnpj.trim() && !formData.companyName.trim()) {
        setFeedback({
          isOpen: true,
          message: 'Preencha pelo menos um campo: CNPJ ou Razão Social.',
          statusOk: false
        })
        return
      }

      if (formData.contribuinteIcms === 'CONTRIBUINTE' && !formData.stateRegistration.trim()) {
        setFeedback({
          isOpen: true,
          message: 'Inscrição Estadual obrigatória para contribuinte do ICMS.',
          statusOk: false
        })
        return
      }
    } else {
      if (formData.cpf.trim() && !formData.contactName.trim()) {
        setFeedback({
          isOpen: true,
          message: 'Quando CPF é preenchido, o nome da pessoa é obrigatório.',
          statusOk: false
        })
        return
      }

      if (!formData.cpf.trim() && !formData.contactName.trim()) {
        setFeedback({
          isOpen: true,
          message: 'Preencha pelo menos um campo: CPF ou Nome.',
          statusOk: false
        })
        return
      }
    }

    if (isMain && !formData.cnpj.trim()) {
      setFeedback({
        isOpen: true,
        message: 'Para cadastrar uma empresa própria, o preenchimento do CNPJ é obrigatório.',
        statusOk: false
      })
      return
    }

    if (!isMain && (!formData.companyTypes || formData.companyTypes.length === 0)) {
      setFeedback({
        isOpen: true,
        message: 'O tipo de empresa é obrigatório.',
        statusOk: false
      })
      return
    }

    const dataToSend: any = {
      legalPerson: legalPerson,
      isMain: isMain
    }

    if (legalPerson) {
      dataToSend.cnpj = formData.cnpj && formData.cnpj.trim() ? formData.cnpj.trim() : null
      dataToSend.cpf = null
      dataToSend.companyName = formData.companyName && formData.companyName.trim() ? formData.companyName.trim() : null
      dataToSend.contactName = null
      dataToSend.stateRegistration = formData.stateRegistration && formData.stateRegistration.trim() ? formData.stateRegistration.trim() : null
      dataToSend.contribuinteIcms = formData.contribuinteIcms || null
    } else {
      dataToSend.cpf = formData.cpf && formData.cpf.trim() ? formData.cpf.trim() : null
      dataToSend.cnpj = null
      dataToSend.contactName = formData.contactName && formData.contactName.trim() ? formData.contactName.trim() : null
      dataToSend.companyName = null
      dataToSend.stateRegistration = null
      dataToSend.contribuinteIcms = null
    }

    dataToSend.email = formData.email && formData.email.trim() ? formData.email.trim() : null
    dataToSend.phone = formData.phone && formData.phone.trim() ? formData.phone.trim() : null

    if (formData.activedDays.length > 0) {
      dataToSend.activedDays = formData.activedDays
    }
    if (formData.functions.length > 0) {
      dataToSend.functions = formData.functions
    }
    dataToSend.contacts = formData.contacts
    dataToSend.linkedUsers = formData.linkedUsers

    if (formData.startTime && formData.startTime.trim()) {
      dataToSend.startTime = formData.startTime.trim()
    } else {
      dataToSend.startTime = null
    }
    if (formData.endTime && formData.endTime.trim()) {
      dataToSend.endTime = formData.endTime.trim()
    } else {
      dataToSend.endTime = null
    }

    if (formData.companyTypes && formData.companyTypes.length > 0) {
      dataToSend.companyTypes = { id: formData.companyTypes.map((ct: any) => ct.id) }
    }

    const hasValidAddress = formData.cep?.trim() ||
      formData.state?.name?.trim() ||
      formData.city?.name?.trim() ||
      formData.neighborhood?.name?.trim() ||
      formData.street?.trim() ||
      formData.number?.trim() ||
      formData.complement?.trim()

    if (hasValidAddress) {
      if (formData.cep?.trim()) dataToSend.cep = formatCep(formData.cep.trim())
      if (formData.state?.name?.trim()) dataToSend.state = formData.state
      if (formData.city?.name?.trim()) dataToSend.city = formData.city
      if (formData.neighborhood?.name?.trim()) dataToSend.neighborhood = formData.neighborhood
      if (formData.street?.trim()) dataToSend.street = formData.street.trim()
      if (formData.number?.trim()) dataToSend.number = formData.number.trim()
      if (formData.complement?.trim()) dataToSend.complement = formData.complement.trim()
    }

    dataToSend.deliveryAddresses = buildDeliveryAddressesPayload(formData.deliveryAddresses, formatCep)

    console.log('🛰️ [FRONT] update company payload', dataToSend)
    ApiService.companies.update(Number(companyId), dataToSend)
      .then((response: any) => {
        setModalActive(false)
        setNewCompanies?.(prev => prev + 1)
        queryClient.invalidateQueries({ queryKey: ['companies'] })
        setFeedback({
          isOpen: true,
          message: 'Empresa atualizada com sucesso!',
          statusOk: true
        })
        navigate('..')
      })
      .catch((error: any) => {
        console.error('Erro ao atualizar empresa:', error)
        setNewCompanies?.(prev => prev + 1)
        const msg = error?.response?.data?.message || error?.response?.data?.error || 'Erro ao atualizar empresa. Tente novamente.'
        setFeedback({
          isOpen: true,
          message: msg,
          statusOk: false
        })
      })
  }

  const handleSaveCompany = () => {
    if (legalPerson) {
      if (formData.cnpj.trim() && !formData.companyName.trim()) {
        setFeedback({
          isOpen: true,
          message: 'Quando CNPJ é preenchido, a razão social é obrigatória.',
          statusOk: false
        })
        return
      }

      if (!formData.cnpj.trim() && !formData.companyName.trim()) {
        setFeedback({
          isOpen: true,
          message: 'Preencha pelo menos um campo: CNPJ ou Razão Social.',
          statusOk: false
        })
        return
      }

      if (formData.contribuinteIcms === 'CONTRIBUINTE' && !formData.stateRegistration.trim()) {
        setFeedback({
          isOpen: true,
          message: 'Inscrição Estadual obrigatória para contribuinte do ICMS.',
          statusOk: false
        })
        return
      }
    } else {
      if (formData.cpf.trim() && !formData.contactName.trim()) {
        setFeedback({
          isOpen: true,
          message: 'Quando CPF é preenchido, o nome da pessoa é obrigatório.',
          statusOk: false
        })
        return
      }

      if (!formData.cpf.trim() && !formData.contactName.trim()) {
        setFeedback({
          isOpen: true,
          message: 'Preencha pelo menos um campo: CPF ou Nome.',
          statusOk: false
        })
        return
      }
    }

    if (isMain && !formData.cnpj.trim()) {
      setFeedback({
        isOpen: true,
        message: 'Para cadastrar uma empresa própria, o preenchimento do CNPJ é obrigatório.',
        statusOk: false
      })
      return
    }

    if (!isMain && (!formData.companyTypes || formData.companyTypes.length === 0)) {
      setFeedback({
        isOpen: true,
        message: 'O tipo de empresa é obrigatório.',
        statusOk: false
      })
      return
    }

    const dataToSend: any = {
      legalPerson: legalPerson,
      isMain: isMain
    }

    if (legalPerson) {
      dataToSend.cnpj = formData.cnpj && formData.cnpj.trim() ? formData.cnpj.trim() : null
      dataToSend.cpf = null
      dataToSend.companyName = formData.companyName && formData.companyName.trim() ? formData.companyName.trim() : null
      dataToSend.contactName = null
      dataToSend.stateRegistration = formData.stateRegistration && formData.stateRegistration.trim() ? formData.stateRegistration.trim() : null
      dataToSend.contribuinteIcms = formData.contribuinteIcms || null
    } else {
      dataToSend.cpf = formData.cpf && formData.cpf.trim() ? formData.cpf.trim() : null
      dataToSend.cnpj = null
      dataToSend.contactName = formData.contactName && formData.contactName.trim() ? formData.contactName.trim() : null
      dataToSend.companyName = null
      dataToSend.stateRegistration = null
      dataToSend.contribuinteIcms = null
    }

    dataToSend.email = formData.email && formData.email.trim() ? formData.email.trim() : null
    dataToSend.phone = formData.phone && formData.phone.trim() ? formData.phone.trim() : null

    if (formData.activedDays.length > 0) {
      dataToSend.activedDays = formData.activedDays
    }
    if (formData.functions.length > 0) {
      dataToSend.functions = formData.functions
    }
    dataToSend.contacts = formData.contacts
    dataToSend.linkedUsers = formData.linkedUsers

    if (formData.startTime && formData.startTime.trim()) {
      dataToSend.startTime = formData.startTime.trim()
    } else {
      dataToSend.startTime = null
    }
    if (formData.endTime && formData.endTime.trim()) {
      dataToSend.endTime = formData.endTime.trim()
    } else {
      dataToSend.endTime = null
    }

    if (formData.companyTypes && formData.companyTypes.length > 0) {
      dataToSend.companyTypes = { id: formData.companyTypes.map((ct: any) => ct.id) }
    }

    const hasValidAddress = formData.cep?.trim() ||
      formData.state?.name?.trim() ||
      formData.city?.name?.trim() ||
      formData.neighborhood?.name?.trim() ||
      formData.street?.trim() ||
      formData.number?.trim() ||
      formData.complement?.trim()

    if (hasValidAddress) {
      if (formData.cep?.trim()) dataToSend.cep = formatCep(formData.cep.trim())
      if (formData.state?.name?.trim()) dataToSend.state = formData.state
      if (formData.city?.name?.trim()) dataToSend.city = formData.city
      if (formData.neighborhood?.name?.trim()) dataToSend.neighborhood = formData.neighborhood
      if (formData.street?.trim()) dataToSend.street = formData.street.trim()
      if (formData.number?.trim()) dataToSend.number = formData.number.trim()
      if (formData.complement?.trim()) dataToSend.complement = formData.complement.trim()
    }

    dataToSend.deliveryAddresses = buildDeliveryAddressesPayload(formData.deliveryAddresses, formatCep)

    console.log('🛰️ [FRONT] create company payload', dataToSend)
    ApiService.companies.create(dataToSend)
      .then((response: any) => {
        setModalActive(false)
        setNewCompanies?.(prev => prev + 1)
        queryClient.invalidateQueries({ queryKey: ['companies'] })
        setFeedback({
          isOpen: true,
          message: 'Empresa criada com sucesso!',
          statusOk: true
        })
        if (onSuccess) {
          onSuccess(response.data)
        } else {
          navigate('..')
        }
      })
      .catch((error: any) => {
        console.error('Erro ao criar empresa:', error)
        const msg = error?.response?.data?.message || error?.response?.data?.error || 'Erro ao criar empresa. Tente novamente.'
        setFeedback({
          isOpen: true,
          message: msg,
          statusOk: false
        })
      })
  }

  return (
    <ModalTest>
      <Container>
        <Header>
          <h2>{companyId ? 'Editar Empresa' : 'Cadastrar Empresa'}</h2>
          <CloseDiv onClick={() => { if (onCancel) { onCancel(); } else { navigate('..'); } }}>
            <img src={close} />
          </CloseDiv>
        </Header>

        <div style={{ display: 'flex', gap: '24px', marginBottom: '16px' }}>
          <SwitchDiv style={{ marginBottom: 0 }}>
            <InputLabel>Pessoa jurídica</InputLabel>
            <SwitchBox>
              <SwitchInput
                type="checkbox"
                disabled={isMain}
                onChange={(e) => {
                  const checked = e.target.checked
                  setLegalPerson(checked)
                  if (!checked) {
                    setFormData(prev => ({
                      ...prev,
                      cnpj: '',
                      companyName: '',
                      stateRegistration: '',
                      contribuinteIcms: '',
                      contactName: prev.contactName || prev.companyName || '',
                    }))
                  } else {
                    setFormData(prev => ({
                      ...prev,
                      cpf: '',
                      contactName: '',
                      companyName: prev.companyName || prev.contactName || '',
                    }))
                  }
                }}
                checked={legalPerson} />
              <SwitchSlider className="slider"></SwitchSlider>
            </SwitchBox>
          </SwitchDiv>

          <SwitchDiv style={{ marginBottom: 0 }}>
            <InputLabel>Empresa própria</InputLabel>
            <SwitchBox>
              <SwitchInput
                type="checkbox"
                onChange={(e) => {
                  const checked = e.target.checked
                  setIsMain(checked)
                  if (checked) {
                    setLegalPerson(true)
                    setFormData(prev => ({
                      ...prev,
                      cpf: '',
                      companyTypes: [{ typeName: 'Própria', id: 4 }]
                    }))
                  } else {
                    setFormData(prev => ({
                      ...prev,
                      companyTypes: []
                    }))
                  }
                }}
                checked={isMain} />
              <SwitchSlider className="slider"></SwitchSlider>
            </SwitchBox>
          </SwitchDiv>
        </div>
        <FormDataDiv>
          {sectionsInModal.map((section, index) => (
            <React.Fragment key={section.sectionName ?? index}>
              <SectionDiv className={section.className}>
                {section.sectionName === 'address' && (
                  <SectionHeaderRow style={{ gridColumn: '1 / -1', width: '100%' }}>
                    <SectionTitle>Endereço de faturamento</SectionTitle>
                  </SectionHeaderRow>
                )}
                {section.inputs
                  .filter((input) => {

                    if (legalPerson) {
                      if (input.value === 'cpf' || input.value === 'contactName') {
                        return false;
                      }
                      return true;
                    }

                    if (input.value === 'cnpj' || input.value === 'stateRegistration' || input.value === 'companyName' || input.value === 'contribuinteIcms') {
                      return false;
                    }
                    return true;
                  })
                  .map((input, inputIndex) =>
                    <InputDiv key={inputIndex}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <InputLabel style={{ marginBottom: 0 }}>
                          {input.value === 'contactName' ? 'Nome' : input.label}
                        </InputLabel>
                        {isMain && (input.value === 'companyName' || input.value === 'contactName') && (
                          <span style={{
                            backgroundColor: '#e0f2fe',
                            color: '#0369a1',
                            fontSize: '11px',
                            fontWeight: '600',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            textTransform: 'uppercase'
                          }}>
                            {formData.cnpj ? (formData.cnpj.replace(/\D/g, '').substring(8, 12) === '0001' ? 'Matriz' : 'Filial') : ''}
                          </span>
                        )}
                      </div>
                      {input.value === 'activedDays' ?
                        <DisponibilityComponent company={formData} setCompany={setFormData} />
                        : input.value === 'functions' ?
                          <SelectFunctionsComponent company={formData} setCompany={setFormData} />
                          : input.value === 'companyTypes' ?
                            <SelectTypeComponent company={formData} setCompany={setFormData} disabled={isMain} />
                            : input.value === 'cep' ?
                              <InsertCEPComponent company={formData} setCompany={setFormData} setAutoAddress={setAutoAddress} autoAddress={autoAddress} />
                              : input.value === 'contribuinteIcms' ?
                                <InputData
                                  as="select"
                                  value={formData.contribuinteIcms}
                                  onChange={(e) => handleEditForm({ target: { value: e.target.value } }, 'contribuinteIcms')}
                                >
                                  <option value="">Selecione...</option>
                                  <option value="CONTRIBUINTE">Contribuinte</option>
                                  <option value="ISENTO">Isento</option>
                                  <option value="NAO_CONTRIBUINTE">Não contribuinte</option>
                                </InputData>
                              :
                              <InputData
                                type={input.type}
                                placeholder={input.placeholder}
                                readOnly={
                                  (input.value === 'state' && autoAddress.state) ||
                                  (input.value === 'city' && autoAddress.city) ||
                                  (input.value === 'neighborhood' && autoAddress.neighborhood) ||
                                  (input.value === 'street' && autoAddress.street)
                                }
                                onChange={(e) => {
                                  const maskedValue = input.value === 'cnpj'
                                    ? maskCNPJ(e.target.value)
                                    : input.value === 'cpf'
                                      ? maskCPF(e.target.value)
                                      : input.value === 'phone'
                                        ? maskPhone(e.target.value)
                                        : e.target.value
                                  handleEditForm({ target: { value: maskedValue } }, input.value as keyof CompanyInterface)
                                }}
                                onBlur={input.value === 'cnpj' ? handleCnpjBlur : undefined}
                                value={String(handleViewValue(input.value as keyof CompanyInterface))}
                              />
                      }
                    </InputDiv>
                  )}
              </SectionDiv>

              {section.sectionName === 'address' && (
                <SectionDiv style={{ flexDirection: 'column' }}>
                  <SectionHeaderRow>
                    <SectionTitle>Endereços de entrega</SectionTitle>
                    <Plus strokeWidth={2.5} size={18} color="#0091d4" style={{ cursor: 'pointer' }} onClick={handleNewDeliveryAddress} />
                  </SectionHeaderRow>
                  {newDeliveryAddress ? (
                    <SectionDiv className="address" style={{ width: '100%' }}>
                      <InputDiv>
                        <InputLabel>CEP</InputLabel>
                        <InsertCEPComponent
                          company={deliveryAddressForm as unknown as CompanyInterface}
                          setCompany={setDeliveryAddressForm as unknown as React.Dispatch<React.SetStateAction<CompanyInterface>>}
                          setAutoAddress={setAutoDeliveryAddress}
                          autoAddress={autoDeliveryAddress}
                        />
                      </InputDiv>
                      {deliveryAddressInputs.map((input) => (
                        <InputDiv key={input.field}>
                          <InputLabel>{input.label}</InputLabel>
                          <InputData
                            type={input.type}
                            placeholder={input.placeholder}
                            readOnly={
                              (input.field === 'state' && autoDeliveryAddress.state) ||
                              (input.field === 'city' && autoDeliveryAddress.city) ||
                              (input.field === 'neighborhood' && autoDeliveryAddress.neighborhood) ||
                              (input.field === 'street' && autoDeliveryAddress.street)
                            }
                            onChange={(e) => handleEditDeliveryAddressField(input.field as keyof DeliveryAddressInterface, e.target.value)}
                            value={
                              input.field === 'state' ? deliveryAddressForm.state.name :
                                input.field === 'city' ? deliveryAddressForm.city.name :
                                  input.field === 'neighborhood' ? deliveryAddressForm.neighborhood.name :
                                    String(deliveryAddressForm[input.field as keyof DeliveryAddressInterface] || '')
                            }
                          />
                        </InputDiv>
                      ))}
                      <OptionsContainer style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '0.2rem' }}>
                        <Save onClick={handleAddDeliveryAddress} size={18} color="#0091d4" strokeWidth={1.5} style={{ cursor: 'pointer' }} />
                      </OptionsContainer>
                    </SectionDiv>
                  ) : null}
                  <ContactsContainer>
                    {formData.deliveryAddresses.length === 0 ? (
                      <EmptyContactsMessage>
                        Nenhum endereço de entrega cadastrado ainda. Clique no ícone + para adicionar.
                      </EmptyContactsMessage>
                    ) : (
                      formData.deliveryAddresses.map((address, addressIndex) => (
                        <ContactCard key={address.id ?? addressIndex}>
                          <ContactHeader>
                            <ContactName>{formatDeliveryAddressSummary(address)}</ContactName>
                            <button
                              onClick={() => handleRemoveDeliveryAddress(addressIndex)}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: '#dc3545',
                                cursor: 'pointer',
                                padding: '0.3rem',
                                borderRadius: '4px',
                                fontSize: '0.8rem',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#f8d7da';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }}
                              title="Remover endereço de entrega"
                            >
                              ✕
                            </button>
                          </ContactHeader>
                        </ContactCard>
                      ))
                    )}
                  </ContactsContainer>
                </SectionDiv>
              )}
            </React.Fragment>
          ))}
          <SectionDiv style={{ flexDirection: 'column' }}>
            <SectionHeaderRow>
              <SectionTitle>Contatos</SectionTitle>
              <Plus strokeWidth={2.5} size={18} color="#0091d4" style={{ cursor: 'pointer' }} onClick={handleNewContact} />
            </SectionHeaderRow>
            {
              newContact ?
                <><LineContainer>
                  {contactInputs.map((input) => (
                    <InputDiv key={input.field} style={{ gridArea: input.field, marginLeft: '0.5rem' }}>
                      <InputLabel>{input.label}</InputLabel>
                      <InputData
                        type={input.type}
                        onChange={(e) => {
                          handleEditContat(input.field, input.field === 'phone' ? maskPhone(e.target.value) : e.target.value)
                        }}
                        placeholder='Digite aqui...'
                        value={
                          input.field === 'name' ? contactForm.name :
                            input.field === 'email' ? contactForm.email :
                              input.field === 'phone' ? contactForm.phone : ''
                        }
                      />
                    </InputDiv>
                  ))}
                  <OptionsContainer style={{ gridArea: 'save', paddingTop: '0.45rem', cursor: 'pointer', paddingLeft: '0.35rem' }}><Save onClick={handleAddContact} size={18} color="#0091d4" strokeWidth={1.5} /></OptionsContainer>
                </LineContainer></>
                :
                null
            }
            <ContactsContainer>
              {formData.contacts.length === 0 ? (
                <EmptyContactsMessage>
                  Nenhum contato cadastrado ainda. Clique no ícone + para adicionar um novo contato.
                </EmptyContactsMessage>
              ) : (
                formData.contacts.map((contact, index) => (
                  <ContactCard key={index}>
                    <ContactHeader>
                      <ContactName>{contact.name}</ContactName>
                      <button
                        onClick={() => handleRemoveContact(index)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#dc3545',
                          cursor: 'pointer',
                          padding: '0.3rem',
                          borderRadius: '4px',
                          fontSize: '0.8rem',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#f8d7da';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                        title="Remover contato"
                      >
                        ✕
                      </button>
                    </ContactHeader>
                    <ContactInfo>
                      <ContactField>
                        <ContactLabel>Email:</ContactLabel>
                        <ContactValue>{contact.email}</ContactValue>
                      </ContactField>
                      <ContactField>
                        <ContactLabel>Telefone:</ContactLabel>
                        <ContactValue>{contact.phone}</ContactValue>
                      </ContactField>
                    </ContactInfo>
                  </ContactCard>
                ))
              )}
            </ContactsContainer>
          </SectionDiv>
          <SectionDiv style={{ flexDirection: 'column' }}>
            <SectionHeaderRow style={{ marginBottom: 0 }}>
              <SectionTitle>Usuários vinculados</SectionTitle>
            </SectionHeaderRow>
            <CompactUserSelectWrap>
              <UserSelect<{ id: number, name: string }>
                key={userSelectKey}
                label=''
                apiUrl={`${process.env.REACT_APP_GATEWAY_BASE_URL}/backend/user/findAll`}
                displayField='name'
                value={null}
                onChange={handleAddLinkedUser}
              />
            </CompactUserSelectWrap>
            <LinkedUsersTagsContainer>
              {formData.linkedUsers.length === 0 ? (
                <EmptyContactsMessage>
                  Nenhum usuário vinculado ainda. Selecione um usuário para criar o vínculo.
                </EmptyContactsMessage>
              ) : (
                formData.linkedUsers.map((user) => (
                  <LinkedUserTag key={user.id}>
                    {user.name}
                    <button
                      type="button"
                      onClick={() => handleRemoveLinkedUser(user.id)}
                    >
                      x
                    </button>
                  </LinkedUserTag>
                ))
              )}
            </LinkedUsersTagsContainer>
          </SectionDiv>

        </FormDataDiv>

        <BottomDiv>
          <ButtonContainer>
            {companyId ? (
              <ButtonDiv className="delete" onClick={() => { setShowConfirm(true) }}>Excluir</ButtonDiv>
            ) : null}
            <ButtonDiv onClick={() => { if (onCancel) { onCancel(); } else { navigate('..'); } }} className='cancel'>Cancelar</ButtonDiv>
            <ButtonDiv onClick={companyId ? handleEdit : handleSaveCompany}>
              <img src={plus}
              />
              {companyId ? 'Editar' : 'Cadastrar'}
            </ButtonDiv>
          </ButtonContainer>
        </BottomDiv>
      </Container>
      <ConfirmationModal
        isOpen={showConfirm}
        title="Excluir Empresa"
        message="Tem certeza que deseja excluir esta empresa? Esta ação não poderá ser desfeita."
        actions={[
          {
            label: 'Cancelar',
            onClick: () => { setShowConfirm(false) },
            variant: 'outline' as const
          },
          {
            label: 'Excluir',
            onClick: handleDeleteConfirm,
            variant: 'danger' as const
          }
        ]}
      />
    </ModalTest>
  )
}

export default AddCompanyModal